import { Invoice, InvoiceLineItem, Payment } from '../types';
import { InvoiceBuyerProfile, InvoiceSellerProfile } from '../types/invoiceSeller';
import { formatSiretDisplay } from './siret';
import { downloadInvoicePdf } from './invoicePdf';

const addressLinesFromParty = (party: InvoiceSellerProfile | InvoiceBuyerProfile): string[] =>
  [
    party.addressLine1,
    party.addressLine2,
    [party.postalCode, party.city].filter(Boolean).join(' ').trim() || null,
    party.countryCode,
  ].filter((line): line is string => Boolean(line && String(line).trim()));

export function invoiceLinesTotal(lines: InvoiceLineItem[]): number {
  return lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
}

/** PDF payload built at create time — document content is not stored in DB. */
export interface InvoicePdfContext {
  seller: InvoiceSellerProfile;
  buyer: InvoiceBuyerProfile;
  lineItems: InvoiceLineItem[];
  currency: string;
}

export function downloadPaymentInvoicePdf(args: {
  payment: Payment;
  invoice: Invoice;
  seller: InvoiceSellerProfile;
  /** Prefer explicit context from the create form; re-download falls back to payment only. */
  buyer?: InvoiceBuyerProfile;
  lineItems?: InvoiceLineItem[];
  currency?: string;
  invoiceName?: string;
  paymentTerms?: string;
  paymentMethodLabel?: string;
  accountHolder?: string;
  iban?: string;
  bic?: string;
  projectName?: string | null;
  formatDateShort: (date: Date | string) => string;
  formatCurrency: (amount: number, currency?: string) => string;
  t: (key: string) => string;
}): void {
  const {
    payment,
    invoice,
    seller,
    projectName,
    formatDateShort,
    formatCurrency,
    t,
  } = args;

  const buyer = args.buyer || {};
  const currency = args.currency || payment.currency || 'EUR';
  const lines =
    args.lineItems && args.lineItems.length > 0
      ? args.lineItems
      : [
          {
            id: 'fallback',
            description: projectName || '—',
            quantity: 1,
            unitPrice: payment.amount,
          },
        ];
  const total = invoiceLinesTotal(lines);

  const statusLabel =
    payment.status === 'paid'
      ? t('payments.statusLabels.paid')
      : payment.status === 'overdue'
        ? t('payments.statusLabels.overdue')
        : t('payments.statusLabels.sent');

  const sellerName = seller.name?.trim() || seller.company?.trim() || '—';
  const sellerCompany =
    seller.name?.trim() && seller.company?.trim() && seller.company.trim() !== seller.name.trim()
      ? seller.company.trim()
      : undefined;
  const buyerName = buyer.name?.trim() || buyer.company?.trim() || '—';
  const buyerCompany =
    buyer.name?.trim() && buyer.company?.trim() && buyer.company.trim() !== buyer.name.trim()
      ? buyer.company.trim()
      : undefined;

  downloadInvoicePdf({
    invoiceNumber: invoice.invoiceNumber,
    invoiceName: args.invoiceName,
    issuedAtLabel: formatDateShort(invoice.issuedAt),
    fromName: sellerName,
    fromCompany: sellerCompany,
    fromSiret: seller.siret ? formatSiretDisplay(seller.siret) : undefined,
    fromVatNumber: seller.vatNumber || undefined,
    fromAddressLines: addressLinesFromParty(seller),
    toName: buyerName,
    toCompany: buyerCompany,
    toSiret: buyer.siret ? formatSiretDisplay(buyer.siret) : undefined,
    toVatNumber: buyer.vatNumber || undefined,
    toAddressLines: addressLinesFromParty(buyer),
    projectName: projectName || '—',
    amountLabel: formatCurrency(total, currency),
    paymentDateLabel:
      payment.status === 'paid' && payment.paymentDate
        ? formatDateShort(payment.paymentDate)
        : undefined,
    statusLabel,
    notes: invoice.notes,
    paymentTerms: args.paymentTerms,
    paymentMethodLabel: args.paymentMethodLabel,
    accountHolder: args.accountHolder,
    iban: args.iban,
    bic: args.bic,
    lineItems: lines.map((line) => ({
      description: line.description || '—',
      details: line.details,
      quantityLabel: String(line.quantity),
      unitPriceLabel: formatCurrency(line.unitPrice, currency),
      vatLabel:
        line.vatRate === null || line.vatRate === undefined
          ? undefined
          : `${String(line.vatRate).replace('.', ',')} %`,
      totalLabel: formatCurrency(line.quantity * line.unitPrice, currency),
    })),
    labels: {
      title: t('invoices.pdfTitle'),
      from: t('invoices.pdfFrom'),
      to: t('invoices.pdfTo'),
      project: t('invoices.project'),
      amount: t('invoices.pdfAmount'),
      paymentDate: t('invoices.pdfPaymentDate'),
      status: t('invoices.pdfStatus'),
      notes: t('invoices.pdfNotes'),
      paymentTerms: t('invoices.paymentTerms'),
      paymentSection: t('invoices.paymentSection'),
      paymentMethod: t('invoices.paymentMethod'),
      accountHolder: t('invoices.accountHolder'),
      ibanLabel: t('invoices.ibanLabel'),
      bicLabel: t('invoices.bicLabel'),
      siret: t('invoices.pdfSiret'),
      vatNumber: t('invoices.pdfVatNumber'),
      item: t('invoices.itemColumn'),
      qty: t('invoices.qty'),
      unitPrice: t('invoices.unitPrice'),
      vatRate: t('invoices.vatRate'),
      total: t('invoices.total'),
    },
  });
}

/** Map a profiles row to seller fields used on invoices / PDF. */
export function sellerFromProfile(profile: Record<string, unknown> | null | undefined): InvoiceSellerProfile {
  if (!profile) return {};
  return {
    name: typeof profile.name === 'string' ? profile.name : undefined,
    company: typeof profile.company === 'string' ? profile.company : undefined,
    siret: typeof profile.siret === 'string' ? profile.siret : null,
    vatNumber: typeof profile.vat_number === 'string' ? profile.vat_number : null,
    addressLine1: typeof profile.address_line1 === 'string' ? profile.address_line1 : null,
    addressLine2: typeof profile.address_line2 === 'string' ? profile.address_line2 : null,
    postalCode: typeof profile.postal_code === 'string' ? profile.postal_code : null,
    city: typeof profile.city === 'string' ? profile.city : null,
    countryCode: typeof profile.country_code === 'string' ? profile.country_code : null,
  };
}
